
import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { ArrowLeft, User, Bell, Lock, HelpCircle, LogOut, ChevronRight, CheckSquare, Square, Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const Settings: React.FC = () => {
  const { setCurrentScreen, currentUser, toggle2FA, logout } = useApp();
  const { toast } = useToast();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [errors, setErrors] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const handleToggle2FA = () => {
    toggle2FA();
    toast({
      title: currentUser?.twoFactorEnabled ? "2FA Disabled" : "2FA Enabled",
      description: currentUser?.twoFactorEnabled 
        ? "Two-factor authentication has been disabled" 
        : "Two-factor authentication has been enabled for additional security",
    });
  };

  const validatePasswordForm = () => {
    let valid = true;
    const newErrors = { currentPassword: '', newPassword: '', confirmPassword: '' };

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required';
      valid = false;
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
      valid = false;
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
      valid = false;
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validatePasswordForm()) {
      // In a real app, this would make an API call to change the password
      toast({
        title: "Password Changed",
        description: "Your password has been successfully updated",
      });
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const handleLogout = () => {
    logout();
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out",
    });
  };

  return (
    <div className="flex flex-col h-full pb-20">
      <div className="flex items-center mb-6 pt-4">
        <button 
          onClick={() => setCurrentScreen('dashboard')}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold ml-4">Settings</h1>
      </div>

      <div className="px-4">
        {!showChangePassword ? (
          <>
            <div className="mobile-card mb-6">
              <div className="flex items-center mb-6">
                <div className="bg-neobank-lightAccent p-3 rounded-full mr-4">
                  <User className="w-6 h-6 text-neobank-primary" />
                </div>
                <div>
                  <h2 className="font-medium">{currentUser?.email}</h2>
                  <p className="text-sm text-gray-500">{currentUser?.phone}</p>
                </div>
              </div>

              <div className="border-t border-gray-100 -mx-5 px-5 py-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">KYC Status</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    currentUser?.kycStatus === 'approved' 
                      ? 'bg-green-100 text-green-800' 
                      : currentUser?.kycStatus === 'pending' 
                        ? 'bg-amber-100 text-amber-800' 
                        : 'bg-gray-100 text-gray-800'
                  }`}>
                    {currentUser?.kycStatus === 'not_started' 
                      ? 'Not Started' 
                      : currentUser?.kycStatus.charAt(0).toUpperCase() + currentUser?.kycStatus.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mobile-card mb-6">
              <h2 className="font-medium mb-4">Security</h2>

              <div className="space-y-4">
                <button 
                  onClick={() => setShowChangePassword(true)} 
                  className="flex justify-between items-center w-full py-2"
                >
                  <div className="flex items-center">
                    <Lock className="w-5 h-5 mr-3 text-gray-500" />
                    <span>Change Password</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                </button>

                <button 
                  onClick={handleToggle2FA} 
                  className="flex justify-between items-center w-full py-2"
                >
                  <div className="flex items-center">
                    <Lock className="w-5 h-5 mr-3 text-gray-500" />
                    <span>Two-Factor Authentication</span>
                  </div>
                  <div>
                    {currentUser?.twoFactorEnabled ? (
                      <CheckSquare className="w-5 h-5 text-neobank-primary" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>
              </div>
            </div>

            <div className="mobile-card mb-6">
              <h2 className="font-medium mb-4">Notifications</h2>

              <div className="flex justify-between items-center py-2">
                <div className="flex items-center">
                  <Bell className="w-5 h-5 mr-3 text-gray-500" />
                  <span>Push Notifications</span>
                </div>
                <div>
                  <CheckSquare className="w-5 h-5 text-neobank-primary" />
                </div>
              </div>
            </div>

            <div className="mobile-card mb-6">
              <h2 className="font-medium mb-4">Help & Support</h2>

              <button className="flex justify-between items-center w-full py-2">
                <div className="flex items-center">
                  <HelpCircle className="w-5 h-5 mr-3 text-gray-500" />
                  <span>FAQ</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <button 
              onClick={handleLogout} 
              className="flex items-center justify-center text-red-600 font-medium p-3 w-full"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Log Out
            </button>
          </>
        ) : (
          <div className="mobile-card">
            <h2 className="font-medium mb-4">Change Password</h2>

            <form onSubmit={handleChangePassword}>
              <div className="mb-4">
                <label className="input-label" htmlFor="current-password">Current Password</label>
                <div className="relative">
                  <input
                    id="current-password"
                    type={showCurrentPassword ? "text" : "password"}
                    className="input-field pr-10"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.currentPassword && <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>}
              </div>

              <div className="mb-4">
                <label className="input-label" htmlFor="new-password">New Password</label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    className="input-field pr-10"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.newPassword && <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>}
              </div>

              <div className="mb-6">
                <label className="input-label" htmlFor="confirm-password">Confirm New Password</label>
                <input
                  id="confirm-password"
                  type="password"
                  className="input-field"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
              </div>

              <div className="flex gap-3">
                <button 
                  type="button" 
                  className="btn-outline flex-1"
                  onClick={() => setShowChangePassword(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">
                  Update Password
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
